import { CheckIcon, MagicWandIcon } from '@/components/icons';
import { Box, Collapse, Typography } from '@mui/material';
import { useState } from 'react';
import { instructionCardStyles } from './styles';

interface InstructionCardProps {
  title: string;
  instructions: Array<{
    text: string;
    type?: 'green' | 'purple';
  }>;
  collapsible?: boolean;
  defaultOpen?: boolean;
  contentTitle?: string | undefined;
  collapsedTitle?: string | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
}

const InstructionCard: React.FC<InstructionCardProps> = ({
  title,
  instructions,
  collapsible = false,
  defaultOpen = true,
  contentTitle,
  collapsedTitle,
  onOpenChange,
}) => {
  const [open, setOpen] = useState<boolean>(defaultOpen);
  const shouldShow = collapsible ? open : true;
  const displayTitle = collapsible
    ? open
      ? title
      : (collapsedTitle ?? title)
    : title;

  return (
    <Box
      sx={{
        ...instructionCardStyles.container,
        cursor: collapsible ? 'pointer' : 'default',
        padding:
          collapsible && !open
            ? '10px 30px'
            : instructionCardStyles.container.padding,

        marginBottom:
          collapsible && !open
            ? '0px'
            : instructionCardStyles.container.marginBottom,
      }}
      onClick={
        collapsible
          ? () => {
              setOpen(prev => {
                const next = !prev;
                if (onOpenChange) onOpenChange(next);
                return next;
              });
            }
          : undefined
      }
      role={collapsible ? 'button' : undefined}
      aria-expanded={collapsible ? open : undefined}
    >
      <Box
        sx={{
          ...instructionCardStyles.iconContainer,
          marginBottom:
            collapsible && !open
              ? '0px'
              : instructionCardStyles.iconContainer.marginBottom,
          cursor: collapsible ? 'pointer' : 'default',
        }}
      >
        <Box sx={instructionCardStyles.iconBox}>
          <MagicWandIcon width={24} height={24} />
        </Box>
        <Typography variant="h4" sx={instructionCardStyles.title}>
          {displayTitle}
        </Typography>
        {collapsible && (
          <Box
            component="span"
            sx={instructionCardStyles.chevron(open)}
            aria-label={open ? 'Collapse' : 'Expand'}
          >
            {open ? '▾' : '▸'}
          </Box>
        )}
      </Box>

      <Collapse in={shouldShow} timeout={500} unmountOnExit>
        <Box>
          {contentTitle && (
            <Typography variant="h4" sx={instructionCardStyles.contentTitle}>
              {contentTitle}
            </Typography>
          )}
          {instructions.map((instruction, index) => (
            <Box key={index} sx={instructionCardStyles.instructionItem}>
              <Box
                sx={
                  instruction.type === 'purple'
                    ? instructionCardStyles.purpleCheckIcon
                    : instructionCardStyles.greenCheckIcon
                }
              >
                <CheckIcon width={10} height={8} fill="white" />
              </Box>
              <Typography
                variant="body2"
                sx={instructionCardStyles.instructionText}
              >
                {instruction.text}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default InstructionCard;
